import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { Component } from 'react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import { ShareClass } from '../models/entity';
import { DataStore, RatingImageStore, AccessibilityStore } from '../stores';
import { observable, computed } from 'mobx';
import Image from './Image';
import Disclaimers from './Disclaimers';
import Translate from './Translate';
import HTag from './HTag';

const RatingImageWrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;
RatingImageWrapper.defaultProps = {};

const AsAtDate = styled<any, any>('div')`
    ${propStyles};
`;

AsAtDate.defaultProps = {
    flex: '1 0 auto',
    fontSize: 'xs',
    textAlign: ['center', 'left'],
    marginX: 'sm',
    marginY: 'md',
};

const ImageWrapper = styled<any, any>('div')`
    ${propStyles};
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
`;

ImageWrapper.defaultProps = {
    alignSelf: 'center',
    display: 'block',
    width: '100%',
    marginY: ['sm', '0'],
};

const ImageComponent = styled<any, any>(Image)`
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-indent: -100em; /* hides alt text */
`;

ImageComponent.defaultProps = {
    display: 'block',
    width: '100%',
};

const PlaceholderWrapper = styled<any, any>('div')`
    ${propStyles};
`;

interface RatingImageProps {
    // Injected
    theme?: any;
    dataStore?: DataStore;
    accessibilityStore?: AccessibilityStore;
    // --------
    ratingImageStore?: RatingImageStore;
    // Needed if not passing instance of ratingImageStore
    shareClass?: ShareClass;
}

@inject('dataStore', 'accessibilityStore')
@observer
class RatingImage extends Component<RatingImageProps> {
    @observable public ratingImageStore: RatingImageStore;
    // Used to track if the initial image exists and handle the onError
    @observable hasError: boolean;

    constructor(props) {
        super(props);
        const { dataStore, shareClass, ratingImageStore } = this.props;
        this.ratingImageStore =
            ratingImageStore ||
            new RatingImageStore(dataStore.stores, shareClass);
    }

    @computed
    get theme() {
        const { theme } = this.props;
        return theme;
    }
    @computed
    get header(): React.ReactElement {
        const { hasHeadingText, headingText } = this.ratingImageStore;
        if (hasHeadingText) {
            return (
                <HTag
                    componentTheme={'ratingImageHeading'}
                    defaultStyleProps={{
                        color: 'body',
                        fontFamily: 'heading',
                        fontWeight: 'bold',
                        fontSize: 'md',
                    }}
                    level={2}
                    text={headingText}
                />
            );
        }
    }

    @computed
    get asAtDate(): ReactElement {
        const {
            hasAsAtDate,
            asAtDate,
            targetImageExists,
        } = this.ratingImageStore;
        if (hasAsAtDate && targetImageExists) {
            return (
                <AsAtDate {...this.theme.ratingImageAsAtDate}>
                    <Translate>{asAtDate.label}</Translate> {asAtDate.value}
                </AsAtDate>
            );
        }
    }

    @computed
    get ratingImage(): ReactElement {
        const { imageFileExists, existingImage } = this.ratingImageStore;

        if (imageFileExists) {
            return this.imageComponent(existingImage.url, existingImage.name);
        }

        if (this.noImagePlaceholder) {
            return this.noImagePlaceholder;
        }

        if (this.noDataPlaceholder) {
            return this.noDataPlaceholder;
        }
    }

    imageComponent(src: string, fileName: string) {
        const { accessibilityStore } = this.props;
        const { altText } = accessibilityStore;
        return (
            <ImageWrapper {...this.theme.ratingImageContainer}>
                <ImageComponent
                    src={src}
                    altText={altText(fileName)}
                    {...this.theme.ratingImage}
                />
            </ImageWrapper>
        );
    }

    @computed
    get noImagePlaceholder(): ReactElement | undefined {
        const { noImagePlaceholder, imageFileExists } = this.ratingImageStore;
        if (!imageFileExists && noImagePlaceholder) {
            return (
                <PlaceholderWrapper {...this.theme.ratingImagePlaceholder}>
                    <Translate>{noImagePlaceholder}</Translate>
                </PlaceholderWrapper>
            );
        }
        return undefined;
    }

    @computed
    get noDataPlaceholder(): ReactElement | undefined {
        const { noDataPlaceholder } = this.ratingImageStore;
        if (noDataPlaceholder) {
            return (
                <PlaceholderWrapper {...this.theme.ratingImagePlaceholder}>
                    <Translate>{noDataPlaceholder}</Translate>
                </PlaceholderWrapper>
            );
        }
        return undefined;
    }

    @computed
    get disclaimers(): ReactElement {
        const { disclaimers } = this.ratingImageStore;
        return (
            <Disclaimers
                disclaimers={disclaimers}
                {...this.theme.ratingImageDisclaimer}
            />
        );
    }

    render() {
        const { hideComponent, imageFileExists } = this.ratingImageStore;
        if (hideComponent) {
            return null;
        }
        return (
            <RatingImageWrapper {...this.theme.ratingImageWrapper}>
                {this.header}
                {imageFileExists && this.asAtDate}
                {this.ratingImage}
                {imageFileExists && this.disclaimers}
            </RatingImageWrapper>
        );
    }
}

export default withTheme(RatingImage) as any;



// WEBPACK FOOTER //
// ./src/Components/RatingImage.tsx